import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = '';
const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('blc_admin_token'));
  const [loading, setLoading] = useState(true);
  const logoutRef = useRef(null);

  // authFetch: performs authenticated fetch.
  // Only auto-logouts on 401 from the auth/me endpoint (handled in useEffect below).
  // For all other routes, returns the response so callers can handle errors gracefully.
  const authFetch = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers }
    });
    return res;
  }, [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authFetch('/api/admin/auth/me')
      .then(r => {
        if (r.status === 401 || r.status === 403) { logoutRef.current?.(); return null; }
        return r.json();
      })
      .then(data => {
        if (!data) return;
        if (data?.role) setAdmin(data);
        else logoutRef.current?.();
      })
      .catch(() => logoutRef.current?.())
      .finally(() => setLoading(false));
  }, [token, authFetch]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/admin/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    if (!res.ok) throw new Error('Identifiants incorrects');
    const data = await res.json();
    localStorage.setItem('blc_admin_token', data.token);
    setToken(data.token);
    setAdmin(data.user);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('blc_admin_token');
    setToken(null);
    setAdmin(null);
  };

  // Keep logoutRef always pointing to the latest logout function
  logoutRef.current = logout;

  return (
    <AdminContext.Provider value={{ admin, token, loading, login, logout, authFetch, role: admin?.role || null }}>
      {children}
    </AdminContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminContext);
